import { LOOPBAR_CLAMP_VALUE, TICKS_PER_LOOP } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useRef } from 'react'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { GRAY_COLORS } from '../../../lib/stitches'
import { useTreeState } from '../../../stores/Root'
import { undoManager } from '../../../stores/StudioStore'
import { Row } from '../../core/Components'
import { ChevronRight } from '../../core/icons'
import { clampToValue } from '../../core/Utils'
import { gray5, logoBlue } from '../../platform/color'
import ContextMenu from './ContextMenu'

const ArrowRightIcon = ({ fill, size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 11 11"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.88633 9.23864C5.59344 8.94575 5.59344 8.47087 5.88634 8.17798L7.81433 6.25001L1.375 6.25001C0.960786 6.25001 0.625 5.91422 0.625 5.50001C0.625 5.0858 0.960786 4.75001 1.375 4.75001L7.81434 4.75001L5.88634 2.82201C5.59344 2.52911 5.59344 2.05424 5.88634 1.76135C6.17923 1.46845 6.6541 1.46845 6.947 1.76135L10.1553 4.96968C10.296 5.11033 10.375 5.3011 10.375 5.50001C10.375 5.69893 10.296 5.88969 10.1553 6.03034L6.94699 9.23865C6.6541 9.53154 6.17923 9.53153 5.88633 9.23864Z"
      fill={fill}
    />
  </svg>
)

const ArrowLeftIcon = ({ fill, size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 11 11"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.11367 9.23864C4.82077 9.53153 4.3459 9.53154 4.05301 9.23865L0.844673 6.03034C0.704019 5.88969 0.625 5.69893 0.625 5.50001C0.624999 5.3011 0.704017 5.11033 0.84467 4.96968L4.053 1.76135C4.3459 1.46846 4.82077 1.46846 5.11366 1.76135C5.40656 2.05424 5.40656 2.52912 5.11366 2.82201L3.18566 4.75001L9.625 4.75001C10.0392 4.75001 10.375 5.0858 10.375 5.50001C10.375 5.91423 10.0392 6.25001 9.625 6.25001L3.18567 6.25001L5.11366 8.17798C5.40656 8.47087 5.40656 8.94575 5.11367 9.23864Z"
      fill={fill}
    />
  </svg>
)

const useDrag = (
  handleRef: any,
  onMove: (newValue: number) => boolean,
  instrumentLoopWidthPX: number,
  onClick?: () => void
) => {
  const { studio } = useTreeState()

  useEffect(() => {
    const handle = handleRef.current
    if (!handle) return

    let pressed = false
    let pressedX = -1
    let mouseMoveDistance = 0
    handle.onmousedown = (evt) => {
      evt.preventDefault()
      evt.stopPropagation()
      if (evt.which !== 1) return

      undoManager.startGroup(() => {
        pressedX = evt.pageX
        pressed = true
        const onMouseMove = (evt) => {
          evt.preventDefault()
          evt.stopPropagation()

          mouseMoveDistance = Math.max(mouseMoveDistance, evt.pageX - pressedX)
          const delta48n = ((evt.pageX - pressedX) / instrumentLoopWidthPX) * TICKS_PER_LOOP

          let updateOccurred = false
          if (onMove) updateOccurred = onMove(delta48n)
          if (updateOccurred) {
            // need to clamp this value since the loop bar movement/edges are clamped as well
            pressedX = clampToValue(evt.pageX, LOOPBAR_CLAMP_VALUE * instrumentLoopWidthPX)
            if (studio.repeatSection) studio.enableAudioUpdates()
          }
        }
        const onMouseUp = () => {
          pressed = false
          document.removeEventListener('mousemove', onMouseMove)
          if (mouseMoveDistance < 8 && onClick) onClick()

          studio.enableAudioUpdates()
          mouseMoveDistance = 0
          undoManager.stopGroup()
        }
        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp, { once: true })
      })
    }
  }, [onMove, instrumentLoopWidthPX])
}

const LoopBarContextMenu: FC = observer(() => {
  const { studio } = useTreeState()

  const snapToLength = (newLengthInLoops: number) => {
    const currLength = studio.repeatEnd - studio.repeatStart
    const desiredLength = TICKS_PER_LOOP * newLengthInLoops
    const delta48 = desiredLength - currLength
    studio.setRepeatEnd(delta48)
    studio.enableAudioUpdates()
  }

  return (
    <ContextMenu.Portal>
      <ContextMenu.Content>
        <ContextMenu.CheckboxItem
          checked={studio.repeatSection}
          onCheckedChange={studio.toggleRepeatSection}
        >
          Toggle
        </ContextMenu.CheckboxItem>
        <ContextMenu.Sub>
          <ContextMenu.SubTrigger>
            Set Length{' '}
            <ContextMenu.RightSlot>
              <ChevronRight color="white" height="12px" width="12px" />
            </ContextMenu.RightSlot>
          </ContextMenu.SubTrigger>
          <ContextMenu.Portal>
            <ContextMenu.SubContent sideOffset={2} alignOffset={-5}>
              <ContextMenu.Item onClick={() => snapToLength(0.25)}>1/4</ContextMenu.Item>
              <ContextMenu.Item onClick={() => snapToLength(0.5)}>1/2</ContextMenu.Item>
              <ContextMenu.Item onClick={() => snapToLength(1.0)}>1</ContextMenu.Item>
              <ContextMenu.Item onClick={() => snapToLength(2.0)}>2</ContextMenu.Item>
            </ContextMenu.SubContent>
          </ContextMenu.Portal>
        </ContextMenu.Sub>
      </ContextMenu.Content>
    </ContextMenu.Portal>
  )
})

const LoopBarSide = ({ children, style, onResize, instrumentLoopWidthPX, disabled }) => {
  const handleRef = useRef(null)
  useDrag(handleRef, !disabled && onResize, instrumentLoopWidthPX)

  return (
    <div
      ref={handleRef}
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        cursor: !disabled && 'ew-resize',
        ...style
      }}
    >
      {children}
    </div>
  )
}

const LoopBar: FC<any> = observer(
  ({
    start,
    end,
    height,
    repeatOn,
    onStartChange,
    onEndChange,
    onMove,
    instrumentLoopWidthPX,
    disabled,
    toggle
  }) => {
    const handleRef = useRef(null)
    useDrag(handleRef, !disabled && onMove, instrumentLoopWidthPX, toggle)

    return (
      <Row
        style={{
          position: 'absolute',
          left: start,
          width: `calc(${end}px - ${start}px)`,
          height: height,
          backgroundColor: repeatOn ? logoBlue : gray5,
          cursor: !disabled && 'pointer'
        }}
        onDoubleClick={toggle}
        ref={handleRef}
        verticalCenter
        justifySpaceBetween
      >
        <LoopBarSide
          style={{ paddingLeft: '5px' }}
          onResize={onStartChange}
          instrumentLoopWidthPX={instrumentLoopWidthPX}
          disabled={disabled}
        >
          <ArrowRightIcon size={'11px'} fill="white" />
        </LoopBarSide>
        <LoopBarSide
          style={{ paddingRight: '5px' }}
          onResize={onEndChange}
          instrumentLoopWidthPX={instrumentLoopWidthPX}
          disabled={disabled}
        >
          <ArrowLeftIcon size={'11px'} fill="white" />
        </LoopBarSide>
      </Row>
    )
  }
)

interface LoopBarMarkerProps {
  height: string
  instrumentLoopWidthPX: number
}
const LoopBarMarker: FC<LoopBarMarkerProps> = ({ height, instrumentLoopWidthPX }) => {
  const { studio } = useTreeState()
  const song = studio.song

  return (
    <Row
      className="sticky-top"
      style={{
        height: height,
        backgroundColor: GRAY_COLORS.gray01,
        overflowY: 'visible',
        overflowX: 'visible'
      }}
    >
      <ContextMenu.Root>
        <ContextMenu.Trigger>
          <LoopBar
            height={height}
            repeatOn={studio.repeatSection}
            start={(studio.repeatStart / TICKS_PER_LOOP) * instrumentLoopWidthPX}
            end={(studio.repeatEnd / TICKS_PER_LOOP) * instrumentLoopWidthPX}
            onStartChange={studio.setRepeatStart}
            onEndChange={studio.setRepeatEnd}
            onMove={studio.moveRepeatStartAndEnd}
            instrumentLoopWidthPX={instrumentLoopWidthPX}
            toggle={studio.toggleRepeatSection}
            disabled={!ArpRegistry.readyForStudioUse}
          />
        </ContextMenu.Trigger>

        {ArpRegistry.readyForStudioUse && <LoopBarContextMenu />}
      </ContextMenu.Root>
      {Array.from(Array(song.songDisplayLength + studio.extraBarsCount).keys()).map(
        (item, index) => (
          <div
            key={index}
            style={{
              minWidth: `${instrumentLoopWidthPX}px`,
              height: height,
              borderRight: '1px solid var(--studio-medium)',
              borderBottom: '1px solid var(--studio-medium)',
              backgroundColor: GRAY_COLORS.gray01
            }}
          />
        )
      )}
    </Row>
  )
}

export default observer(LoopBarMarker)
